import { useState } from "react";
import { Instagram, Twitter, Facebook, Mail } from "lucide-react";
import Logo from "./Logo";
import { useSettings } from "@/context/SettingsContext";
import JsonLd from "@/components/store/JsonLd";
import api, { API, mediaUrl, SITE_URL, qrTrackUrl } from "@/lib/api";
import { toast } from "sonner";

const DEFAULT_FOOTER = [
  { title: "SHOP", links: [{ label: "All Products", url: "/shop" }, { label: "Dresses", url: "/shop?category=dresses" }, { label: "Swim", url: "/shop?category=swim" }] },
  { title: "ABOUT", links: [{ label: "Our Story", url: "/our-story" }, { label: "Sustainability", url: "/#deadstock" }] },
  { title: "HELP", links: [{ label: "FAQ", url: "/#" }, { label: "Contact Us", url: "/#" }] },
];

export default function Footer() {
  const { settings } = useSettings();
  const [email, setEmail] = useState("");
  const cols = settings?.navigation?.footer?.length ? settings.navigation.footer : DEFAULT_FOOTER;
  const brand = settings?.brand || {};

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try { await api.post("/newsletter/subscribe", { email }); toast.success("Welcome aboard — you're on the list."); setEmail(""); }
    catch { toast.error("Could not subscribe."); }
  };

  return (
    <footer className="bg-linen border-t hairline">
      <JsonLd id="ld-org" data={{
        "@context": "https://schema.org",
        "@type": "Organization",
        name: brand.name || "Sew Voyage",
        url: window.location.origin,
        logo: `${window.location.origin}/logo512.png`,
        description: brand.tagline || "Luxury resort wear reimagined from rescued deadstock fabric — conscious by choice, luxurious by nature.",
      }} />
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 py-16 grid grid-cols-2 md:grid-cols-5 gap-10">
        <div className="col-span-2 md:col-span-1">
          <Logo variant="footer" imgClassName="h-16 md:h-20" />
          <p className="font-heading italic text-lg mt-6 leading-snug text-ink/70">{brand.tagline || <>Conscious by choice.<br/>Luxurious by nature.<br/>Yours to voyage.</>}</p>
          <img src={mediaUrl(settings?.page_images?.brand_mark || "https://static.prod-images.emergentagent.com/jobs/65e76132-927f-451c-9933-4dc061721808/images/a347cbd409dd11d18d240db6cd3998b76442cc5a1a337d2840411b46446ca9a6.jpeg")} alt="" className="sketch w-28 mt-6 opacity-60" />
        </div>
        {cols.map((c, i) => (
          <div key={i}>
            <h4 className="font-body uppercase tracking-luxe text-[0.65rem] mb-5">{c.title}</h4>
            <ul className="space-y-3">
              {c.links.map((l, j) => (
                <li key={j}><a href={l.url} className="font-body text-sm text-ink/60 hover:text-ink transition-colors duration-300">{l.label}</a></li>
              ))}
            </ul>
          </div>
        ))}
        <div>
          <h4 className="font-body uppercase tracking-luxe text-[0.65rem] mb-5">Stay Connected</h4>
          <p className="font-body text-sm text-ink/60 mb-4 leading-relaxed">Be the first to know about new drops, exclusive pieces and stories from the voyage.</p>
          <form onSubmit={subscribe} className="flex border-b border-ink/30">
            <input data-testid="footer-newsletter-input" value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="Email address"
                   className="bg-transparent flex-1 py-2 text-sm outline-none font-body placeholder:text-ink/40" />
            <button data-testid="footer-newsletter-submit" className="icon-thin"><Mail size={18} /></button>
          </form>
          <div className="flex gap-4 mt-6 icon-thin">
            {(brand.instagram || brand.twitter || brand.facebook || brand.contact_email) ? (
              <>
                {brand.instagram && <a href={brand.instagram} target="_blank" rel="noreferrer" aria-label="Instagram"><Instagram size={18} /></a>}
                {brand.twitter && <a href={brand.twitter} target="_blank" rel="noreferrer" aria-label="Twitter"><Twitter size={18} /></a>}
                {brand.facebook && <a href={brand.facebook} target="_blank" rel="noreferrer" aria-label="Facebook"><Facebook size={18} /></a>}
                {brand.contact_email && <a href={`mailto:${brand.contact_email}`} aria-label="Email"><Mail size={18} /></a>}
              </>
            ) : (
              <>
                <a href="#" aria-label="Instagram"><Instagram size={18} /></a>
                <a href="#" aria-label="Twitter"><Twitter size={18} /></a>
                <a href="#" aria-label="Facebook"><Facebook size={18} /></a>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-col items-center pb-14" data-testid="footer-qr">
        <img src={`${API}/qr?data=${encodeURIComponent(qrTrackUrl(SITE_URL, "footer"))}&fill=1a1a1a&bg=transparent&logo=1`} alt="Scan to visit thesewvoyage.com" className="w-48 h-48 opacity-80" data-testid="footer-qr-image" />
        <p className="font-body text-[0.6rem] uppercase tracking-wide2 text-ink/50 mt-3">Scan to visit us</p>
      </div>
      <div className="border-t hairline py-6 text-center">
        <p className="font-body text-[0.65rem] tracking-wide2 text-ink/50 uppercase">© 2026 {brand.name || "Sew Voyage"}. All rights reserved. · <a href="/login" data-testid="footer-admin-link" className="hover:text-ink underline underline-offset-2">Admin Login</a> · by <a href="https://www.tenmoreminutes.com" target="_blank" rel="noreferrer" data-testid="footer-credit-link" className="hover:text-ink underline underline-offset-2">Lawrence Williams</a></p>
      </div>
    </footer>
  );
}
